import { CaptureConsole } from '@sentry/integrations'
import React, {
  Suspense,
  useEffect, useRef, useState,
} from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { CSpinner } from '@coreui/react-pro'
import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import './scss/style.scss'
import { EnvConfig } from 'src/util/EnvConfig'
import { StyleUtil } from 'src/util/StyleUtil'

// routes config
import {
  defaultRoutes,
  marketplaceRoutes,
  noAuthRoutes,
  presentRoutes,
} from './routes'
import {
  useUIStore,
  useUserStore,
} from './store'
import { observer } from 'mobx-react'
import { AuthRoute } from './AuthRoute'
import PresentLayout from './layout/PresentLayout'
import { ExternalAnalyticsUtil } from './util/analytics/ExternalAnalyticsUtil'
import { PresentationsAnalyticsUtil } from './util/analytics/PresentationsAnalyticsUtil'

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))
const ContainerLayout = React.lazy(() => import('./layout/ContainerLayout'))
const MarketplaceLayout = React.lazy(() => import('./layout/MarketplaceLayout'))
const Dashboard = React.lazy(() => import('./views/dashboard/Dashboard'))

const App = observer(() => {

  const uiStore = useUIStore()
  const userStore = useUserStore()

  const [browserZoomLevel, setBrowserZoomLevel] = useState((window.outerWidth - 8) / window.innerWidth)

  const onWindowResize = () => {
    uiStore.setViewportDimensions(window.innerWidth, window.innerHeight)
    setBrowserZoomLevel((window.outerWidth - 8) / window.innerWidth)
  }

  const didBootstrapComplete = useRef(false)

  useEffect(() => {
    if (!didBootstrapComplete.current) {
      didBootstrapComplete.current = true

      Sentry.init({
        dsn: EnvConfig.getSentryDSN(),
        environment: EnvConfig.getBackendEnvironment().toLowerCase(),
        release: `ingage-teams@${EnvConfig.getVersion()}`,
        // @ts-ignore
        integrations: [new BrowserTracing(), new CaptureConsole({
          levels: ['error']
        })],
        tracesSampleRate: 0.0
      })
      if (userStore.profile?.id) {
        Sentry.setUser({ id: userStore.profile?.id })
      }
    }

    window.addEventListener('resize', () => onWindowResize())
    return (() => window.removeEventListener('resize', () => onWindowResize()))
  }, [userStore.profile?.id])

  useEffect(() => {
    PresentationsAnalyticsUtil.registerSuperProperties({
      'Device': 'teams-web',
      'session-id': uuidv4()
    })
  }, [])

  useEffect(() => {
    if (userStore.profile?.id) {
      ExternalAnalyticsUtil.addPerson(userStore.profile.id, {
        '$email': userStore.profile.email,
        '$first_name': userStore.profile.firstName,
        '$last_name': userStore.profile.lastName
      })
      PresentationsAnalyticsUtil.identifyUser(userStore.profile.id)
      PresentationsAnalyticsUtil.registerSuperProperties({
        'Paid': userStore.profile.isPaid,
        'is_internal': userStore.profile.isInternal
      })
    }
  }, [userStore.profile])

  useEffect(() => {
    if (window.innerWidth > StyleUtil.viewportOverrideWidth) {
      uiStore.setIsSidebarOpen(true)
    }
  }, [browserZoomLevel])

  return (
    <BrowserRouter>
      <Suspense fallback={<CSpinner color="primary"/>}>
        <Routes>
          <Route path="/" element={<AuthRoute />}>
            <Route element={<DefaultLayout/>}>
              <Route element={<ContainerLayout />}>
                <Route index element={<Navigate to="/dashboard"/>}/>
                {defaultRoutes.map((route, idx) => {
                  return (
                    route.component && (
                      <Route
                        key={idx}
                        path={route.path}
                        element={<route.component/>}
                      />
                    )
                  )
                })}
              </Route>
            </Route>
            <Route element={<DefaultLayout/>}>
              <Route element={<MarketplaceLayout/>}>
                {marketplaceRoutes.map((route, idx) => {
                  return (
                    route.component && (
                      <Route
                        key={idx}
                        path={route.path}
                        element={<route.component/>}
                      />
                    )
                  )
                })}
              </Route>
            </Route>
            <Route element={<PresentLayout />}>
              {presentRoutes.map((route, idx) => {
                return (
                  route.component && (
                    <Route
                      key={idx}
                      path={route.path}
                      element={<route.component/>}
                    />
                  )
                )
              })}
            </Route>
          </Route>
          {noAuthRoutes.map((route, idx) => {
            return (
              route.component &&
              <Route
                key={idx}
                path={route.path}
                element={<route.component/>}
              />
            )
          })}

          <Route path="*" element={<Dashboard/>}/> {/* if path does not exist */}

        </Routes>
      </Suspense>
    </BrowserRouter>
  )
})
export default App
